import { RequestStatus } from '../../enums';
import { makeAsyncReducerHandlers } from '../../helpers';
import {
  VersionsActionTypes,
  ChaptersActionTypes,
  ProjectsActionTypes,
  PhotosActionTypes,
  ClaudeActionTypes,
} from '../../actions/types';
import ChapterHelpers from '../helpers/chapters.helpers';

const getDataToCompareVersions = makeAsyncReducerHandlers(
  VersionsActionTypes.getDataToCompareVersions,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.chapters,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const addCommentToChapter = makeAsyncReducerHandlers(
  ChaptersActionTypes.addCommentToChapter,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { chapterId, comment } = action.payload;
    const updatedChapters = [...state.items];

    const index = updatedChapters.findIndex(chapter => chapter._id === chapterId);

    updatedChapters[index].comments.push(comment);

    return {
      items: updatedChapters,
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const addReplyToCommentInChapter = makeAsyncReducerHandlers(
  ChaptersActionTypes.addReplyToCommentInChapter,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { chapterId, commentId, reply } = action.payload;
    const comment = state.items.find(chapter => chapter._id === chapterId).comments.find(commentItem => commentItem._id === commentId);
    const newComment = { ...comment };
    newComment.replies.push(reply);

    return {
      items: ChapterHelpers.updateChapterComments(state.items, chapterId, [newComment]),
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const updateReplyToCommentInChapter = makeAsyncReducerHandlers(
  ChaptersActionTypes.updateReplyToCommentInChapter,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { chapterId, commentId, reply } = action.payload;

    const chapterIndex = state.items.findIndex(chapter => chapter._id === chapterId);
    const commentIndex = state.items[chapterIndex].comments.findIndex(comment => comment._id === commentId);
    const replyIndex = state.items[chapterIndex].comments[commentIndex].replies.findIndex(replyItem => replyItem._id === reply._id);
    const comment = state.items[chapterIndex].comments[commentIndex];
    comment.replies[replyIndex] = reply;

    return {
      items: ChapterHelpers.updateChapterComments(state.items, chapterId, [comment]),
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const updateChapter = makeAsyncReducerHandlers(
  ChaptersActionTypes.updateChapter,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { chapter } = action.payload;
    const items = Array.from(state.items);
    const index = items.findIndex(item => item._id === chapter._id);

    if (index === -1) {
      items.push(chapter);
    } else {
      items.splice(index, 1, chapter);
    }

    return {
      items,
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const updateCommentInChapter = makeAsyncReducerHandlers(
  ChaptersActionTypes.updateCommentInChapter,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { chapterId, comment } = action.payload;

    return {
      items: ChapterHelpers.updateChapterComments(state.items, chapterId, [comment]),
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const resolveCommentInChapter = makeAsyncReducerHandlers(
  ChaptersActionTypes.resolveCommentInChapter,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { chapterId, comment } = action.payload;

    return {
      items: ChapterHelpers.updateChapterComments(state.items, chapterId, [comment]),
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const deleteCommentInChapter = makeAsyncReducerHandlers(
  ChaptersActionTypes.deleteCommentInChapter,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { chapterId, comment } = action.payload;

    return {
      items: ChapterHelpers.updateChapterComments(state.items, chapterId, [comment]),
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);


const fetchChaptersAndResourcesByProjectId = makeAsyncReducerHandlers(
  ProjectsActionTypes.fetchChaptersAndResourcesByProjectId,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.chapters,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const fetchChaptersAndResourcesByUser = makeAsyncReducerHandlers(
  ProjectsActionTypes.fetchChaptersAndResourcesByUser,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.chapters,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const deleteOrUndoComments = makeAsyncReducerHandlers(
  ChaptersActionTypes.deleteOrUndoComments,
  state => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { chapterId, comments } = action.payload;
    return {
      items: ChapterHelpers.updateChapterComments(state.items, chapterId, comments),
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const saveChaptersChanges = makeAsyncReducerHandlers(
  ChaptersActionTypes.saveChaptersChanges,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.chapters,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const fetchProofDataByProjectId = makeAsyncReducerHandlers(
  ChaptersActionTypes.fetchProofDataByProjectId,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.chapters,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const fetchProofDataByUserId = makeAsyncReducerHandlers(
  ChaptersActionTypes.fetchProofDataByUserId,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.chapters,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);
const fetchPhotosAndResources = makeAsyncReducerHandlers(
  PhotosActionTypes.fetchPhotosAndResources,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.chapters,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const wrapWithTagsAndSave = makeAsyncReducerHandlers(
  ClaudeActionTypes.wrapWithTagsAndSave,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.chapters,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const generateClaudeReplyWithTranscript = makeAsyncReducerHandlers(
  ClaudeActionTypes.generateClaudeReplyWithTranscript,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

export default {
  getDataToCompareVersions,
  addCommentToChapter,
  fetchChaptersAndResourcesByProjectId,
  fetchChaptersAndResourcesByUser,
  addReplyToCommentInChapter,
  updateReplyToCommentInChapter,
  updateChapter,
  updateCommentInChapter,
  resolveCommentInChapter,
  deleteCommentInChapter,
  deleteOrUndoComments,
  saveChaptersChanges,
  fetchPhotosAndResources,
  fetchProofDataByProjectId,
  fetchProofDataByUserId,
  wrapWithTagsAndSave,
  generateClaudeReplyWithTranscript,
};
